import { useState } from "react";
import { format, isToday, isYesterday, parseISO } from "date-fns";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { TimeLog } from "@/services/time-log.service";
import { formatTime } from "../types";
import { TimeLogForm, type TimeLogValues } from "./TimeLogForm";

function formatLogDate(iso: string): string {
	const d = parseISO(iso);
	if (isToday(d)) return "Today";
	if (isYesterday(d)) return "Yesterday";
	return format(d, "MMM d, yyyy");
}

export function TimeLogRow({
	log,
	editable,
	deleting,
	onUpdate,
	onDelete,
}: {
	log: TimeLog;
	editable: boolean;
	deleting: boolean;
	onUpdate: (values: TimeLogValues) => Promise<void>;
	onDelete: () => void;
}) {
	const [editOpen, setEditOpen] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const author = log.logged_by?.full_name ?? log.logged_by?.email ?? "User";

	return (
		<li className="flex flex-col gap-1 px-3 py-2.5">
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium text-foreground">
					{formatTime(log.duration_minutes)}
				</span>
				<span
					className="text-xs text-muted"
					title={format(parseISO(log.logged_date), "PPPP")}
				>
					{formatLogDate(log.logged_date)}
				</span>
				{editable && !confirming && (
					<div className="ml-auto flex items-center">
						<Popover open={editOpen} onOpenChange={setEditOpen}>
							<PopoverTrigger asChild>
								<Button
									size="icon-xs"
									variant="ghost"
									aria-label="Edit time log"
									className="text-muted hover:text-foreground"
								>
									<Pencil className="h-3.5 w-3.5" />
								</Button>
							</PopoverTrigger>
							<PopoverContent
								align="end"
								className="w-80 p-3"
								onEscapeKeyDown={(e) => e.stopPropagation()}
							>
								<TimeLogForm
									initial={{
										minutes: log.duration_minutes,
										date: log.logged_date,
										note: log.description ?? "",
									}}
									submitLabel="Save"
									onCancel={() => setEditOpen(false)}
									onSubmit={async (v) => {
										await onUpdate(v);
										setEditOpen(false);
									}}
								/>
							</PopoverContent>
						</Popover>
						<Button
							size="icon-xs"
							variant="ghost"
							aria-label="Delete time log"
							disabled={deleting}
							onClick={() => setConfirming(true)}
							className="text-muted hover:text-danger"
						>
							{deleting ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<Trash2 className="h-3.5 w-3.5" />
							)}
						</Button>
					</div>
				)}
			</div>
			<span className="truncate text-xs text-muted">{author}</span>
			{log.description && (
				<p className="line-clamp-3 whitespace-pre-wrap break-words text-xs text-foreground">
					{log.description}
				</p>
			)}
			{confirming && (
				<div className="mt-1 flex items-center justify-between gap-2 rounded-md bg-danger-subtle px-2 py-1.5">
					<span className="text-xs text-danger">Delete this entry?</span>
					<div className="flex gap-1">
						<Button
							size="xs"
							variant="ghost"
							onClick={() => setConfirming(false)}
						>
							Keep
						</Button>
						<Button
							size="xs"
							variant="destructive"
							onClick={() => {
								setConfirming(false);
								onDelete();
							}}
						>
							Delete
						</Button>
					</div>
				</div>
			)}
		</li>
	);
}
